/**
 * @prettier
 */
import i18next from 'i18next';
import { phone } from 'phone';
import { ocrVerification } from '../../config/ocrVerification';

// Validation utility functions
const validateRequired = (value, fieldName) => {
    const trimmedValue = String(value || '').trim();
    if (!trimmedValue) {
        return i18next.t('idpal_field_is_required');
    }
    return null;
};

const validateLength = (value, min, max) => {
    const trimmedValue = String(value || '').trim();
    if (min && trimmedValue.length < min) {
        return i18next.t('idpal_minimum_characters_error', { count: min });
    }
    if (max && trimmedValue.length > max) {
        return i18next.t('idpal_maximum_characters_error', { count: max });
    }
    return null;
};

const validateRegex = (value, regex, message) => {
    const trimmedValue = String(value || '').trim();
    if (!new RegExp(regex, 'u').test(trimmedValue)) {
        return i18next.t(message);
    }
    return null;
};

const validateDate = (value, minDate) => {
    if (!value) return null;

    // Check for correct format yyyy-mm-dd (as formatted by formatDateValue)
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const matches = value.match(dateRegex);

    if (!matches) {
        return i18next.t('idpal_invalid_date_error');
    }

    const [, year, month, day] = matches;

    // Convert to numbers for validation
    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);

    // Check if parts are valid numbers
    if (dayNum === 0 || monthNum === 0 || yearNum === 0) {
        return i18next.t('idpal_invalid_date_error');
    }

    // Check if month and day are valid
    if (monthNum < 1 || monthNum > 12) {
        return i18next.t('idpal_invalid_date_error');
    }

    // Get last day of the month
    const lastDay = new Date(yearNum, monthNum, 0).getDate();
    if (dayNum < 1 || dayNum > lastDay) {
        return i18next.t('idpal_invalid_date_error');
    }

    // Create date object for comparison
    const formDate = new Date(yearNum, monthNum - 1, dayNum); // month is 0-based in Date constructor
    if (!(formDate instanceof Date && isFinite(formDate))) {
        return i18next.t('idpal_invalid_date_error');
    }

    if (minDate && formDate < new Date(minDate)) {
        return i18next.t('idpal_minimum_date_error', { date: minDate });
    }

    if (formDate > new Date()) {
        return i18next.t('idpal_maximum_date_error');
    }

    return null;
};

const validatePhone = value => {
    if (!value) return null;
    if (!phone('+' + value).isValid) {
        return i18next.t('idpal_please_enter_valid_number');
    }
    return null;
};

// Step-specific validation functions
const validatePersonalDetails = (formData, options = {}) => {
    const {
        personalInfoOptional = false,
        contactInfoOptional = false,
        showPersonalInfo = true,
        showContactInfo = true,
    } = options;

    const errors = {};
    const { firstName, lastName, dob, phone: phoneNumber, email } = formData;

    // Only validate personal information fields if they should be shown
    if (showPersonalInfo) {
        // First Name validation
        if (ocrVerification.ocrFirstName.required && !personalInfoOptional) {
            const requiredError = validateRequired(firstName, 'firstName');
            if (requiredError) {
                errors.firstName = requiredError;
            }
        }

        // Validate firstName if it's provided, regardless of whether it's required
        if (firstName && String(firstName).trim()) {
            const lengthError = validateLength(
                firstName,
                ocrVerification.ocrFirstName.min,
                ocrVerification.ocrFirstName.max
            );
            if (lengthError) {
                errors.firstName = lengthError;
            } else {
                const regexError = validateRegex(
                    firstName,
                    ocrVerification.ocrFirstName.regex,
                    ocrVerification.ocrFirstName.messages.regex
                );
                if (regexError) errors.firstName = regexError;
            }
        }

        // Last Name validation
        if (ocrVerification.ocrLastName.required && !personalInfoOptional) {
            const requiredError = validateRequired(lastName, 'lastName');
            if (requiredError) {
                errors.lastName = requiredError;
            }
        }

        // Validate lastName if it's provided, regardless of whether it's required
        if (lastName && String(lastName).trim()) {
            const lengthError = validateLength(
                lastName,
                ocrVerification.ocrLastName.min,
                ocrVerification.ocrLastName.max
            );
            if (lengthError) {
                errors.lastName = lengthError;
            } else {
                const regexError = validateRegex(
                    lastName,
                    ocrVerification.ocrLastName.regex,
                    ocrVerification.ocrLastName.messages.regex
                );
                if (regexError) errors.lastName = regexError;
            }
        }

        // Date of Birth validation
        if (ocrVerification.ocrDob.required && !personalInfoOptional) {
            const requiredError = validateRequired(dob, 'idpal_dob');
            if (requiredError) {
                errors.dob = requiredError;
            }
        }

        // Validate DOB if it's provided, regardless of whether it's required
        if (dob && String(dob).trim()) {
            const dateError = validateDate(dob, ocrVerification.ocrDob.minDate);
            if (dateError) errors.dob = dateError;
        }
    }

    // Only validate contact information fields if they should be shown
    if (showContactInfo) {
        // Phone validation
        if (ocrVerification.ocrPhone.required && !contactInfoOptional) {
            const requiredError = validateRequired(phoneNumber, 'phone');
            if (requiredError) {
                errors.phone = requiredError;
            }
        }

        // Validate phone if it's provided, regardless of whether it's required
        if (phoneNumber && String(phoneNumber).trim()) {
            const phoneError = validatePhone(phoneNumber);
            if (phoneError) errors.phone = phoneError;
        } // Email validation
        if (ocrVerification.ocrEmail?.required && !contactInfoOptional) {
            // Check required validation first and prioritize it
            const requiredError = validateRequired(email, 'email');
            if (requiredError) {
                errors.email = requiredError;
            }
        }

        // Validate email if it's provided, regardless of whether it's required
        if (email && String(email).trim()) {
            const lengthError = validateLength(
                email,
                ocrVerification.ocrEmail.min,
                ocrVerification.ocrEmail.max
            );
            if (lengthError) {
                errors.email = lengthError;
            } else {
                const regexError = validateRegex(
                    email,
                    ocrVerification.ocrEmail.regex,
                    ocrVerification.ocrEmail.messages.regex
                );
                if (regexError) errors.email = regexError;
            }
        }
    }

    return errors;
};

const validateAddressDetails = (formData, customFields = [], options = {}) => {
    const { addressInfoOptional = false, showAddressInfo = true } = options;

    const errors = {};

    // Skip validation if address info shouldn't be shown
    if (!showAddressInfo) {
        return errors;
    }
    
    const { country, address1, address2, city, postCode, customField, county } = formData;

    // Country validation
    if (ocrVerification.ocrCountry.required && !addressInfoOptional) {
        const countryError = validateRequired(country, 'country');
        if (countryError) errors.country = countryError;
    }

    // Address validation
    if (ocrVerification.ocrAddress1.required && !addressInfoOptional) {
        const requiredError = validateRequired(address1, 'address1');
        if (requiredError) {
            errors.address1 = requiredError;
        }
    }

    // Validate address1 if it's provided, regardless of whether it's required
    if (address1 && String(address1).trim()) {
        const lengthError = validateLength(
            address1,
            ocrVerification.ocrAddress1.min,
            ocrVerification.ocrAddress1.max
        );
        if (lengthError) {
            errors.address1 = lengthError;
        } else {
            const regexError = validateRegex(
                address1,
                ocrVerification.ocrAddress1.regex,
                ocrVerification.ocrAddress1.messages.regex
            );
            if (regexError) errors.address1 = regexError;
        }
    }

    if (address2) {
        const lengthError = validateLength(
            address2,
            ocrVerification.ocrAddress2.min,
            ocrVerification.ocrAddress2.max
        );
        if (lengthError) {
            errors.address2 = lengthError;
        } else {
            const regexError = validateRegex(
                address2,
                ocrVerification.ocrAddress2.regex,
                ocrVerification.ocrAddress2.messages.regex
            );
            if (regexError) errors.address2 = regexError;
        }
    }

    // City validation
    if (ocrVerification.ocrCity.required && !addressInfoOptional) {
        const requiredError = validateRequired(city, 'city');
        if (requiredError) {
            errors.city = requiredError;
        }
    }

    // Validate city if it's provided, regardless of whether it's required
    if (city && String(city).trim()) {
        const lengthError = validateLength(
            city,
            ocrVerification.ocrCity.min,
            ocrVerification.ocrCity.max
        );
        if (lengthError) {
            errors.city = lengthError;
        } else {
            const regexError = validateRegex(
                city,
                ocrVerification.ocrCity.regex,
                ocrVerification.ocrCity.messages.regex
            );
            if (regexError) errors.city = regexError;
        }
    }
    
    // Validate county if it's provided, regardless of whether it's required
    if (county && String(county).trim()) {
        const lengthError = validateLength(county, ocrVerification.ocrCounty.min, ocrVerification.ocrCounty.max);
        if (lengthError) {
            errors.county = lengthError;
        } else {
            const regexError = validateRegex(county, ocrVerification.ocrCounty.regex, ocrVerification.ocrCounty.messages.regex);
            if (regexError) errors.county = regexError;
        }
    }

    // Post Code validation
    if (ocrVerification.ocrPostCode.required && !addressInfoOptional) {
        const requiredError = validateRequired(postCode, 'postCode');
        if (requiredError) {
            errors.postCode = requiredError;
        }
    }

    // Validate postCode if it's provided, regardless of whether it's required
    if (postCode && String(postCode).trim()) {
        const lengthError = validateLength(
            postCode,
            ocrVerification.ocrPostCode.min,
            ocrVerification.ocrPostCode.max
        );
        if (lengthError) {
            errors.postCode = lengthError;
        } else {
            const regexError = validateRegex(
                postCode,
                ocrVerification.ocrPostCode.regex,
                ocrVerification.ocrPostCode.messages.regex
            );
            if (regexError) errors.postCode = regexError;
        }
    }

    // Custom Field validation
    const customFieldErrors = validateCustomField(
        country,
        customField,
        customFields,
        addressInfoOptional
    );
    if (Object.keys(customFieldErrors).length > 0) {
        return { ...errors, ...customFieldErrors };
    }

    return errors;
};

export const validateCustomField = (
    country,
    customField,
    customFields,
    addressInfoOptional
) => {
    const errors = {};

    if (country && customFields?.length > 0) {
        const customFieldConfig = customFields.find(
            field => field.countryName === country
        );

        if (customFieldConfig) {
            // Required field validation
            if (customFieldConfig.required && !addressInfoOptional) {
                const requiredError = validateRequired(
                    customField,
                    'customField'
                );
                if (requiredError) {
                    errors.customField = requiredError;
                    return errors;
                }
            }

            // Only validate if field has a value
            if (customField && String(customField).trim()) {
                // Length validation
                const lengthError = validateLength(
                    customField,
                    customFieldConfig.min,
                    customFieldConfig.max
                );
                if (lengthError) {
                    errors.customField = lengthError;
                    return errors;
                }

                // Regex validation if regex is provided
                if (customFieldConfig.regex) {
                    const regexError = validateRegex(
                        customField,
                        customFieldConfig.regex,
                        customFieldConfig.messages?.regex ||
                            'idpal_invalid_custom_field'
                    );
                    if (regexError) {
                        errors.customField = regexError;
                        return errors;
                    }
                }
            }
        }
    }

    return errors;
};

const getOcrFieldName = fieldName => {
    if (!fieldName) return '';
    // Regular case: prefix with 'ocr'
    return `ocr${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
};

// Main validation function
export const validateOcrVerification = (
    formData,
    customFields = [],
    step = null,
    options = {}
) => {
    // Extract validation options
    const {
        personalInfoOptional = false,
        contactInfoOptional = false,
        addressInfoOptional = false,
        showPersonalInfo = true,
        showContactInfo = true,
        showAddressInfo = true,
    } = options;

    // If step is specified, use step-specific validation
    if (step === 0) {
        return validatePersonalDetails(formData, {
            personalInfoOptional,
            contactInfoOptional,
            showPersonalInfo,
            showContactInfo,
        });
    } else if (step === 1) {
        return validateAddressDetails(formData, customFields, {
            addressInfoOptional,
            showAddressInfo,
        });
    } else {
        // If no step specified, validate individual field
        const errors = {};
        const fieldName = Object.keys(formData)[0];
        const value = formData[fieldName];
        const ocrFieldName = getOcrFieldName(fieldName);
        const rule = ocrVerification[ocrFieldName];

        if (!rule) return errors;

        // Check if the field should be shown based on the options
        if (
            fieldName === 'firstName' ||
            fieldName === 'lastName' ||
            fieldName === 'dob'
        ) {
            if (!showPersonalInfo) return errors;
        }

        if (fieldName === 'email' || fieldName === 'phone') {
            if (!showContactInfo) return errors;
        }

        if (
            [
                'country',
                'address1',
                'address2',
                'city',
                'postCode',
                'customField',
                'county'
            ].includes(fieldName)
        ) {
            if (!showAddressInfo) return errors;
        }

        if (rule.required) {
            // Skip required validation if the field is in an optional section
            const isOptionalField =
                (personalInfoOptional &&
                    ['firstName', 'lastName', 'dob'].includes(fieldName)) ||
                (contactInfoOptional &&
                    ['email', 'phone'].includes(fieldName)) ||
                (addressInfoOptional &&
                    [
                        'country',
                        'address1',
                        'address2',
                        'city',
                        'postCode',
                        'customField',
                        'county'
                    ].includes(fieldName));

            if (!isOptionalField) {
                const requiredError = validateRequired(value, fieldName);
                if (requiredError) {
                    errors[fieldName] = requiredError;
                    return errors;
                }
            }
        }

        // Only continue with further validation if the value is not empty
        if (value && String(value).trim()) {
            if (rule.min || rule.max) {
                const lengthError = validateLength(value, rule.min, rule.max);
                if (lengthError) {
                    errors[fieldName] = lengthError;
                    return errors;
                }
            }

            if (rule.regex) {
                const regexError = validateRegex(
                    value,
                    rule.regex,
                    rule.messages.regex
                );
                if (regexError) {
                    errors[fieldName] = regexError;
                    return errors;
                }
            }
        }

        if (rule.type === 'date') {
            const dateError = validateDate(value, rule.minDate);
            if (dateError) {
                errors[fieldName] = dateError;
                return errors;
            }
        }

        if (rule.type === 'phone') {
            const phoneError = validatePhone(value);
            if (phoneError) {
                errors[fieldName] = phoneError;
                return errors;
            }
        }

        return errors;
    }
};
